import { theme } from '@Guidelines/Theme/theme';

/** One state's full recipe: the badge's pale pair, the card rail's solid fill, the progress bar's
 * own fill (a CSS `background`, not always a flat color — see `not_addressed`), and its note. */
export type FieldVerificationPresentation = {
	readonly badgeSurface: string;
	readonly badgeInk: string;
	readonly railColor: string;
	readonly barFill: string;
	readonly note: string | null;
};

const PRESENTATION_BY_VERIFICATION_STATUS: Readonly<Record<string, FieldVerificationPresentation>> = {
	verified: {
		badgeSurface: theme.states.success.surface,
		badgeInk: theme.states.success.ink,
		railColor: theme.states.success.solid,
		barFill: theme.states.success.solid,
		note: null,
	},
	corrected: {
		badgeSurface: theme.states.success.surface,
		badgeInk: theme.states.success.ink,
		railColor: theme.states.success.solid,
		barFill: theme.states.success.solid,
		note: null,
	},
	to_verify: {
		badgeSurface: theme.states.warning.surface,
		badgeInk: theme.states.warning.ink,
		railColor: theme.states.warning.solid,
		barFill: theme.states.warning.solid,
		note: null,
	},
	unverified: {
		badgeSurface: theme.states.danger.surface,
		badgeInk: theme.states.danger.ink,
		railColor: theme.states.danger.solid,
		barFill: theme.states.danger.solid,
		note: "Aucune source n'a pu être rattachée à cette valeur.",
	},
	not_addressed: {
		badgeSurface: theme.colors.surfaceMuted,
		badgeInk: theme.colors.inkMuted,
		railColor: theme.colors.trackMuted,
		barFill: `repeating-linear-gradient(90deg, ${theme.colors.track} 0 3px, transparent 3px 6px)`,
		note: "Le sujet n'a été abordé dans aucune source.",
	},
};

/**
 * The single presentation table: every reader — the card's rail and badge, the source panel's badge,
 * the document screen's aggregate progress bar — calls this resolver rather than re-deciding a tint
 * from the raw state value. An unrecognized value falls back to the neutral recipe rather than
 * throwing.
 */
export const resolveFieldVerificationPresentation = (statusValue: string): FieldVerificationPresentation =>
	PRESENTATION_BY_VERIFICATION_STATUS[statusValue] ?? {
		badgeSurface: theme.colors.surfaceMuted,
		badgeInk: theme.colors.inkMuted,
		railColor: theme.colors.trackMuted,
		barFill: theme.colors.trackMuted,
		note: null,
	};

/**
 * The single note a field ever shows: a rejected value always displays its own note, never alongside
 * the state's — the two would otherwise contradict each other for a field whose value both failed its
 * type check and carries the resulting `unverified` state.
 */
export const resolveFieldNote = (field: {
	readonly verification: { readonly value: string };
	readonly isRejectedValue: boolean;
}): string | null => {
	if (field.isRejectedValue) {
		return "Une valeur a été produite mais ne correspond pas au type de ce champ : elle n'a pas été conservée. Saisissez-la si elle est juste.";
	}

	return resolveFieldVerificationPresentation(field.verification.value).note;
};

export default resolveFieldVerificationPresentation;
